import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { Observable, Subject, filter } from 'rxjs';

/**
 * Which independent session scope a cross-tab auth event belongs to.
 *
 * The MBRS app hosts two *independent* authentication concepts in the same
 * browser origin:
 *   - `'customer'` — the cookie-backed customer / admin session (`/auth/login`).
 *   - `'dev'`      — the Bearer-JWT developer-portal session (`/dev-{slug}/login`).
 *
 * Every broadcast carries its scope so each receiver can decide for itself:
 *   - A `'logout'` is same-scope only — it must NEVER tear down tabs in the
 *     other scope (a customer logout leaves dev tabs alone, and vice-versa).
 *   - A `'login'` drives BOTH a same-scope follow (other login-page tabs of the
 *     same scope reload into the app) AND a cross-scope teardown (the two areas
 *     are mutually exclusive per browser, so signing in to one signs the other
 *     out). Receivers therefore filter on `scope` — some on their OWN scope,
 *     some on the OTHER — depending on which behaviour they own.
 */
export type CrossTabAuthScope = 'customer' | 'dev';

/**
 * Notify-only payload broadcast when one tab changes the auth state — either a
 * `'logout'` (go to the login page) or a `'login'` (a sibling tab just signed
 * in; follow it into the app).
 *
 * IMPORTANT — no credentials on the wire. Per the CLAUDE.md hard rule
 * ("NEVER persist auth state — tokens / isLoggedIn / user — in
 * localStorage/sessionStorage; the HttpOnly cookie / in-memory signal are the
 * only sources of truth"), this message carries ONLY routing metadata. It tells
 * the other tabs "a logout/login happened in <scope>, react accordingly"; it
 * does not, and must not, carry a token, user id, or session value. The
 * receiving tab decides what to do via its own guards/`AuthService` and
 * re-verifies the real HttpOnly cookie — it does not trust any payload content
 * beyond `type` and `scope`.
 */
export interface CrossTabAuthMessage {
  type: 'logout' | 'login' | 'password-reset';
  /** Session scope the event belongs to — receivers filter on this. */
  scope: CrossTabAuthScope;
  /** UUID of the sender tab, used to filter self-echoes. */
  tabId: string;
  /** Epoch millis at the time of broadcast. Diagnostics only; not used for ordering. */
  timestamp: number;
  /** Present only on `'password-reset'` — see {@link CrossTabPasswordResetOutcome}. */
  passwordReset?: CrossTabPasswordResetOutcome;
}

/**
 * Outcome of a submitted "Set New Password" form, broadcast so a sibling tab
 * still sitting on the same form follows its sibling to the result page.
 *
 * Carries NO credentials: no password and no reset token. `email` is only the
 * key a receiver uses to tell "my reset link" from an unrelated one (two tabs
 * resetting two different accounts must never move each other). `status` and
 * `errors` are exactly what the result page already reads from navigation
 * state, so the receiver can render the same Success / Failed / Expired screen.
 */
export interface CrossTabPasswordResetOutcome {
  email: string;
  status: boolean;
  errors: string[];
}

/**
 * CrossTabAuthSyncService
 *
 * Cross-tab auth transport. Carries two one-shot events across tabs of the
 * SAME session scope:
 *   - `'logout'` — the user logged out in one tab, so every OTHER tab sends
 *     itself to its login page.
 *   - `'login'`  — the user logged IN in one tab, so every OTHER tab still
 *     sitting on a login page follows it into the app (see {@link login$}).
 * Uses BroadcastChannel when available, falling back to the `storage` window
 * event otherwise.
 *
 * **Notify-only, zero-token.** The payload carries no credentials — only
 * `{ type, scope, tabId, timestamp }`. BroadcastChannel persists nothing; the
 * storage fallback writes-then-immediately-removes a sentinel key so nothing
 * durable is ever stored (and repeated identical payloads still fire a
 * `storage` event because `newValue` toggles). This upholds the CLAUDE.md rule
 * that no auth state is ever persisted to web storage. A `'login'` receiver
 * never trusts the payload as proof of authentication — it reloads and lets
 * `redirectIfAuthenticatedGuard` re-verify the real HttpOnly cookie.
 *
 * **Why separate from {@link CrossTabSyncService} (SRP).** That service is
 * tightly scoped to filing-form state synchronisation — its channel, debounce
 * tuning, and stream keys all serve that one concern. Auth logout is a
 * different reason-to-change (a one-shot security event, not a keystroke
 * burst), so it gets its own channel (`mbrs-auth-sync`), its own sentinel key,
 * and no debounce. Mixing the two would couple unrelated lifecycles.
 *
 * **Scope filtering.** Customer and dev sessions are independent. Every event
 * carries its scope and each receiver filters on the scope it cares about —
 * `logout$` receivers filter their OWN scope (a logout never crosses scopes),
 * while `login$` receivers split: the auth-layout "follow" receivers filter
 * their own scope, and the app-layout "mutual-exclusion" receivers filter the
 * OTHER scope. The logout broadcaster (AuthService.logout) captures the scope
 * from the `isDev` signal at logout time; login broadcasts pass the scope of
 * the funnel that just signed in (`'customer'` for `login()`, `'dev'` for
 * `devLogin()`).
 *
 * **Self-echo filtering.** Both transports already avoid delivering a tab its
 * own message (by spec), but every message still carries the sender `tabId` so
 * `dispatchRemote` can defensively drop any self-echo (e.g. if both transports
 * ever coexist in tests) before it reaches either `logout$` or `login$`.
 *
 * **NgZone.** Both transports fire outside Angular's zone. This service
 * re-enters the zone via `NgZone.run(...)` in the shared `dispatchRemote` —
 * before emitting on the internal stream feeding `logout$` / `login$` — so
 * downstream `router.navigate` / `forceLogout` / signal writes schedule change
 * detection correctly.
 */
@Injectable({ providedIn: 'root' })
export class CrossTabAuthSyncService {
  private static readonly CHANNEL_NAME = 'mbrs-auth-sync';
  /** Storage key used only as a signaling mechanism on the fallback path. */
  private static readonly STORAGE_SIGNAL_KEY = '__mbrs-auth-sync-signal__';

  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly tabId: string = this.generateTabId();
  private readonly channel: BroadcastChannel | null;
  /**
   * Single internal stream of all remote auth events (self-echoes already
   * filtered, re-entered into the Angular zone). The public `logout$` / `login$`
   * streams are derived from it by `type`, so the transport and self-echo logic
   * live in exactly one place.
   */
  private readonly messageSubject = new Subject<CrossTabAuthMessage>();

  /** Bound reference so we can detach the listener on destroy. */
  private readonly storageListener = (ev: StorageEvent) =>
    this.handleStorageEvent(ev);

  /**
   * Stream of remote logout messages. Receivers should additionally `.filter()`
   * on their own {@link CrossTabAuthScope}.
   */
  public readonly logout$: Observable<CrossTabAuthMessage> =
    this.messageSubject.pipe(filter((message) => message.type === 'logout'));

  /**
   * Stream of remote login messages — a sibling tab in the same scope just
   * signed in. Receivers (the auth layouts) should additionally `.filter()` on
   * their own {@link CrossTabAuthScope}, then reload so
   * `redirectIfAuthenticatedGuard` re-verifies the shared HttpOnly cookie and
   * forwards the tab into the app. Never treat receipt as proof of auth.
   */
  public readonly login$: Observable<CrossTabAuthMessage> =
    this.messageSubject.pipe(filter((message) => message.type === 'login'));

  /**
   * Stream of remote password-reset outcomes — a sibling tab just submitted its
   * "Set New Password" form. Receivers must match `passwordReset.email` against
   * their own reset link before following.
   */
  public readonly passwordReset$: Observable<CrossTabAuthMessage> =
    this.messageSubject.pipe(filter((message) => message.type === 'password-reset'));

  constructor() {
    // Feature-detect BroadcastChannel. Prefer it over the storage fallback so
    // we never double-emit when both transports could see the same event.
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel(CrossTabAuthSyncService.CHANNEL_NAME);
      this.channel.onmessage = (ev: MessageEvent<CrossTabAuthMessage>) => {
        this.dispatchRemote(ev.data);
      };
    }
    else {
      this.channel = null;
      window.addEventListener('storage', this.storageListener);
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(
    //   '[Cross-Tab | Debug] service init',
    //   JSON.stringify({ tabId: this.tabId, transport: this.channel ? 'broadcastchannel' : 'storage' })
    // );

    this.destroyRef.onDestroy(() => this.teardown());
  }

  /**
   * Broadcast a logout to every other tab in the given scope. Called only from
   * `AuthService.logout()` (the single user-initiated logout funnel) — never
   * from a receiver's teardown path, so there is zero echo-storm risk.
   *
   * @param scope which session scope (`'customer'` | `'dev'`) just logged out.
   */
  broadcastLogout(scope: CrossTabAuthScope): void {
    this.broadcast('logout', scope);
  }

  /**
   * Broadcast a login to every other tab in the given scope. Called only from
   * the login funnels (`AuthService.login()` → `'customer'`,
   * `AuthService.devLogin()` → `'dev'`) on a *successful* sign-in. Other
   * tabs sitting on a login page reload and let `redirectIfAuthenticatedGuard`
   * forward them into the app. Receivers do not re-broadcast, so there is zero
   * echo-storm risk.
   *
   * @param scope which session scope (`'customer'` | `'dev'`) just logged in.
   */
  broadcastLogin(scope: CrossTabAuthScope): void {
    this.broadcast('login', scope);
  }

  /**
   * Broadcast the outcome of a submitted password reset to every other
   * customer-scope tab. Called only from `ResetPasswordComponent` after it has
   * navigated to the result page. Receivers do not re-broadcast.
   */
  broadcastPasswordReset(outcome: CrossTabPasswordResetOutcome): void {
    this.broadcast('password-reset', 'customer', outcome);
  }

  /** Shared transport for all event types — single place that touches the wire. */
  private broadcast(
    type: CrossTabAuthMessage['type'],
    scope: CrossTabAuthScope,
    passwordReset?: CrossTabPasswordResetOutcome
  ): void {
    const message: CrossTabAuthMessage = {
      type,
      scope,
      tabId: this.tabId,
      timestamp: Date.now(),
      ...(passwordReset ? { passwordReset } : {}),
    };

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log(
    //   '[CrossTabAuthSync] Sending: ',
    //   JSON.stringify({ type, scope, fromTab: this.tabId, via: this.channel ? 'broadcastchannel' : 'storage' })
    // );

    if (this.channel) {
      try {
        this.channel.postMessage(message);
      }
      catch (error) {
        // Channel can throw on non-structured-cloneable data; our payload is
        // plain data, so this is defensive only.
        console.error('[CrossTabAuthSync] Failed to postMessage:', error);
      }
      return;
    }

    // Storage-event fallback: write-then-remove a sentinel key whose value is
    // the serialized payload. Other tabs' `storage` handlers parse
    // `ev.newValue`. The writer's own window does not fire the event, so no
    // self-filter is needed here. Removing immediately means a repeated
    // identical event still fires a `storage` event (newValue must change)
    // AND nothing durable is left in storage (upholds the no-persist rule).
    try {
      localStorage.setItem(CrossTabAuthSyncService.STORAGE_SIGNAL_KEY, JSON.stringify(message));
      localStorage.removeItem(CrossTabAuthSyncService.STORAGE_SIGNAL_KEY);
    }
    catch (error) {
      console.error(
        '[CrossTabAuthSync] Failed to signal via storage fallback:',
        error
      );
    }
  }

  /** Return the current tab's UUID. Exposed for tests and diagnostics. */
  getTabId(): string {
    return this.tabId;
  }

  private dispatchRemote(message: CrossTabAuthMessage | null | undefined): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log('[CrossTabAuthSync] RECV raw', JSON.stringify({ thisTab: this.tabId, message }));

    if (
      !message ||
      (message.type !== 'logout' &&
        message.type !== 'login' &&
        message.type !== 'password-reset')
    )
      return;

    // Self-filter: shouldn't occur on either transport, but guard anyway.
    if (message.tabId === this.tabId) {
      // TEMP DEBUG — remove after root cause found.
      // console.log('[CrossTabAuthSync] RECV dropped (self-echo)', this.tabId);
      return;
    }

    // ----------------------
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // TEMP DEBUG — remove after root cause found.
    // console.log('[CrossTabAuthSync] RECV emitting to messageSubject', message.type, message.scope);
    // ----------------------
    // Re-enter Angular's zone before emitting so downstream subscribers
    // (router.navigate, signal writes, window.location.reload) schedule
    // change detection / run in the expected context.
    this.zone.run(() => this.messageSubject.next(message));
  }

  private handleStorageEvent(ev: StorageEvent): void {
    if (ev.key !== CrossTabAuthSyncService.STORAGE_SIGNAL_KEY)
      return;

    // removal event from our own writer
    if (!ev.newValue)
      return;

    try {
      const parsed = JSON.parse(ev.newValue) as CrossTabAuthMessage;
      this.dispatchRemote(parsed);
    }
    catch (error) {
      console.error(
        '[CrossTabAuthSync] Failed to parse storage-event payload:',
        error
      );
    }
  }

  private teardown(): void {
    if (this.channel) {
      try {
        this.channel.close();
      }
      catch {
        // Ignore — channel may already be closed.
      }
    }
    else {
      window.removeEventListener('storage', this.storageListener);
    }
    this.messageSubject.complete();
  }

  private generateTabId(): string {
    const cryptoRef =
      typeof globalThis !== 'undefined' ? (globalThis as any).crypto : undefined;
    if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
      return cryptoRef.randomUUID();
    }
    // Fallback: non-cryptographic but sufficient for self-identification.
    return `tab-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2, 10)}`;
  }
}
